import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AppShell from "../components/AppShell";
import Avatar from "../components/Avatar";
import EmployeeFormDialog from "../components/EmployeeFormDialog";
import ContractFormDialog from "../components/ContractFormDialog";
import WageRateFormDialog from "../components/WageRateFormDialog";
import RecurringPayrollItems from "../components/RecurringPayrollItems";
import StaffLeaveTab from "../components/StaffLeaveTab";
import UnattachedPayoutBanner from "../components/UnattachedPayoutBanner";
import BreastfeedingPeriodCard, { BreastfeedingChip } from "../components/BreastfeedingPeriodCard";
import { useAuth } from "../contexts/AuthContext";
import useModules from "../hooks/useModules";
import { api, formatApiError } from "../lib/api";
import { useHasPermission } from "../contexts/PermissionsContext";
import {
    CONTRACT_STATUS_LABEL,
    CONTRACT_TYPE_LABEL,
    EMPLOYEE_STATUS_LABEL,
    EMPLOYMENT_BADGE,
    EMPLOYMENT_TYPE_LABEL,
    SALARY_TYPE_LABEL,
    WORKER_SUBTYPE_LABEL,
    daysUntil,
    formatCurrency,
    formatDate,
    formatDateTime,
    statusBadgeClass,
} from "../lib/domain";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import RowActions from "../components/RowActions";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { ArrowLeft, Eye, EyeOff, Fingerprint, Landmark, Pencil, Plus, Send, Trash2 } from "lucide-react";
import { toast } from "sonner";
import DateInput from "../components/DateInput";

export default function StaffProfile() {
    const { id, moduleCode } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { byId: modulesById } = useModules();
    const [employee, setEmployee] = useState(null);
    // Iter59.p12 — pass the employee's module_id into the permission hook
    // so a Dept Manager scoped to that module sees the Edit/Delete controls.
    // `employee` is loaded async, so `null` is fine until the fetch resolves;
    // once resolved the hook re-runs with the module id and unlocks the CTA.
    const canEdit = useHasPermission("employees.write", employee?.module_id || null);
    const canDelete = useHasPermission("employees.delete", employee?.module_id || null);
    const [contracts, setContracts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editOpen, setEditOpen] = useState(false);
    const [contractOpen, setContractOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    // Inline error state — set when the initial employee/contracts load fails.
    // Populated with { message, status, endpoint, timestamp } for the Report-Issue dialog.
    const [loadError, setLoadError] = useState(null);
    const [reportIssueOpen, setReportIssueOpen] = useState(false);

    const load = async () => {
        setLoading(true);
        setLoadError(null);
        try {
            const [e, c] = await Promise.all([
                api.get(`/employees/${id}`),
                api.get(`/contracts?employee_id=${id}`),
            ]);
            setEmployee(e.data);
            setContracts(c.data);
        } catch (err) {
            // No more bounce-to-/staff. Keep the operator on the page and let the
            // inline ErrorCard offer Retry + Report Issue. That way we can SEE
            // where the 500 happened rather than losing the context.
            const ep = err?.config?.baseURL
                ? `${err.config.baseURL}${err.config.url || ""}`
                : err?.config?.url || "unknown";
            setLoadError({
                message: formatApiError(err),
                status: err?.response?.status || 0,
                endpoint: ep,
                timestamp: new Date().toISOString(),
            });
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

// eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        load();
    }, [id]);

    const onDelete = async () => {
        try {
            await api.delete(`/employees/${id}`);
            toast.success("Employee terminated");
            navigate(moduleCode ? `/m/${moduleCode}/staff` : "/", { replace: true });
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    if (loading || (!employee && !loadError)) {
        return (
            <AppShell title="Employee">
                <div
                    className="asl-card p-6 text-sm text-[color:var(--asl-muted)]"
                    data-testid="staff-profile-loading"
                >
                    Loading employee…
                </div>
            </AppShell>
        );
    }

    if (loadError) {
        const ctx = `Endpoint: ${loadError.endpoint}\nStatus:   ${loadError.status}\nWhen:     ${loadError.timestamp}\nMessage:  ${loadError.message}\nEmployee: ${id}\nUser agent: ${navigator.userAgent}`;
        return (
            <AppShell title="Employee">
                <div
                    className="asl-card p-8 max-w-2xl mx-auto text-center space-y-4"
                    data-testid="staff-profile-error-card"
                >
                    <div className="mx-auto h-12 w-12 rounded-full bg-red-50 flex items-center justify-center text-2xl asl-crimson-text">!</div>
                    <div>
                        <h2 className="text-lg font-semibold asl-navy-text mb-1">We couldn&apos;t load this employee&apos;s details.</h2>
                        <p className="text-sm text-[color:var(--asl-muted)]">
                            The server returned an error while loading. Our team has been notified. You can retry, or copy the technical context below and send it to your admin.
                        </p>
                        <p className="mono text-[11px] text-[color:var(--asl-muted)] mt-3">
                            {loadError.status} · {loadError.endpoint.replace(/^https?:\/\/[^/]+/, "")}
                        </p>
                    </div>
                    <div className="flex items-center justify-center gap-3 pt-2">
                        <button
                            type="button"
                            onClick={load}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-md asl-navy-bg text-white text-sm font-semibold hover:opacity-90"
                            data-testid="staff-profile-retry-btn"
                        >
                            Retry
                        </button>
                        <button
                            type="button"
                            onClick={() => setReportIssueOpen(true)}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-[color:var(--asl-crimson)] text-[color:var(--asl-crimson)] text-sm font-semibold hover:bg-[color:var(--asl-crimson-50,#FDF2F5)]"
                            data-testid="staff-profile-report-issue-btn"
                        >
                            Report issue
                        </button>
                        <button
                            type="button"
                            onClick={() => navigate(moduleCode ? `/m/${moduleCode}/staff` : "/")}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-[color:var(--asl-border,#E5E7EB)] text-[color:var(--asl-muted)] text-sm hover:bg-gray-50"
                            data-testid="staff-profile-back-btn"
                        >
                            Back to Staff Directory
                        </button>
                    </div>
                </div>
                <Dialog open={reportIssueOpen} onOpenChange={(o) => !o && setReportIssueOpen(false)}>
                    <DialogContent data-testid="staff-profile-report-dialog">
                        <DialogHeader>
                            <DialogTitle>Report this issue</DialogTitle>
                        </DialogHeader>
                        <div className="text-sm space-y-3">
                            <p>Copy this technical context and paste it into a chat or email to your admin. It contains everything the engineer needs to reproduce and fix the problem.</p>
                            <textarea
                                readOnly
                                value={ctx}
                                className="w-full h-40 border rounded-md p-2 mono text-[11px] bg-gray-50 border-[color:var(--asl-border,#E5E7EB)]"
                                data-testid="staff-profile-report-text"
                                onFocus={(e) => e.target.select()}
                            />
                        </div>
                        <DialogFooter>
                            <button
                                type="button"
                                onClick={() => setReportIssueOpen(false)}
                                className="px-3 py-1.5 rounded-md border border-[color:var(--asl-border,#E5E7EB)] text-sm"
                            >
                                Close
                            </button>
                            <button
                                type="button"
                                onClick={async () => {
                                    try {
                                        await navigator.clipboard.writeText(ctx);
                                        toast.success("Copied to clipboard.");
                                    } catch { toast.error("Could not copy. Please select and copy manually."); }
                                }}
                                className="px-3 py-1.5 rounded-md asl-navy-bg text-white text-sm font-semibold"
                                data-testid="staff-profile-report-copy"
                            >
                                Copy to clipboard
                            </button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </AppShell>
        );
    }

    const moduleName = modulesById[employee.module_id]?.name || "—";

    return (
        <AppShell title="Employee Profile">
            <EmployeeFormDialog
                open={editOpen}
                onOpenChange={setEditOpen}
                employee={employee}
                onSaved={load}
            />
            <ContractFormDialog
                open={contractOpen}
                onOpenChange={setContractOpen}
                employee={employee}
                onSaved={load}
            />

            <section className="space-y-5" data-testid="staff-profile-page">
                <div className="flex items-center justify-between">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                            // History-aware Back: honor the browser stack when the user
                            // came from Attendance / Leave / etc. within this SPA.
                            // Fall back to the module staff directory on direct URL loads.
                            const sameOriginRef = document.referrer && document.referrer.startsWith(window.location.origin);
                            if ((window.history.state && window.history.state.idx > 0) || sameOriginRef) {
                                navigate(-1);
                            } else {
                                navigate(moduleCode ? `/m/${moduleCode}/staff` : "/");
                            }
                        }}
                        data-testid="staff-profile-back"
                    >
                        <ArrowLeft className="h-4 w-4 mr-1" /> Back
                    </Button>
                    <div className="flex items-center gap-2">
                        {canEdit && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setEditOpen(true)}
                                data-testid="staff-profile-edit"
                            >
                                <Pencil className="h-4 w-4 mr-1" /> Edit
                            </Button>
                        )}
                        {canDelete && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setDeleteOpen(true)}
                                data-testid="staff-profile-delete"
                                className="text-[color:var(--asl-crimson)] border-[color:var(--asl-crimson)]/30 hover:bg-[color:var(--asl-crimson)]/5"
                            >
                                <Trash2 className="h-4 w-4 mr-1" /> Terminate
                            </Button>
                        )}
                    </div>
                </div>

                {/* Header card */}
                <div className="asl-card p-6">
                    <div className="flex items-start gap-5">
                        <Avatar src={employee.photo_url} name={employee.full_name} size={84} />
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-3 flex-wrap">
                                <h2 className="text-2xl font-bold asl-navy-text">
                                    {employee.full_name}
                                </h2>
                                {/* Iter60.p49 Item B — EMP-#### removed from the profile header.
                                    Module ID is the sole visible identifier. */}
                                {employee.module_employee_id && (
                                    <span className="mono text-sm asl-crimson-text font-semibold px-2 py-0.5 rounded-full" style={{ background: "var(--asl-bg-alt)" }} data-testid="profile-module-id" title="Module Employee ID">
                                        {employee.module_employee_id}
                                    </span>
                                )}
                                <span
                                    className={`text-xs px-2 py-0.5 rounded-full ${statusBadgeClass(employee.status)}`}
                                >
                                    {EMPLOYEE_STATUS_LABEL[employee.status]}
                                </span>
                                <span
                                    className={`text-xs px-2 py-0.5 rounded-full ${EMPLOYMENT_BADGE[employee.employment_type]}`}
                                >
                                    {EMPLOYMENT_TYPE_LABEL[employee.employment_type]}
                                    {employee.worker_subtype
                                        ? ` · ${WORKER_SUBTYPE_LABEL[employee.worker_subtype]}`
                                        : ""}
                                </span>
                                {/* Iter60.p65 — Breastfeeding period chip (Art. 130). */}
                                <BreastfeedingChip employee={employee} />
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4 text-sm">
                                <Fact label="Job Title" value={employee.job_title} />
                                <Fact label="Module" value={moduleName} />
                                <Fact label="Phone" value={employee.phone} mono />
                                <Fact label="Email" value={employee.email || "—"} mono />
                                <Fact label="National ID" value={employee.national_id} mono />
                                <Fact label="Worksite" value={employee.worksite || "—"} />
                                <Fact label="Hire Date" value={formatDate(employee.hire_date)} />
                                <Fact label="Date of Birth" value={formatDate(employee.date_of_birth)} />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Iter54 — ASL ID card expiry banner */}
                <IdCardBanner employee={employee} />

                {/* Iter60.p63 — Unattached annual-leave payout banner (HR/Admin can attach; others see locked note). */}
                <UnattachedPayoutBanner
                    employee={employee}
                    canAttach={user?.role === "kaafi_system_admin" || user?.role === "kaafi_hr_manager"}
                />

                {/* Iter60.p65 — Breastfeeding period card (Art. 130). Shows and manages the
                    2 hrs/day worked-time credit; skips render for male employees. */}
                <BreastfeedingPeriodCard
                    employee={employee}
                    canEdit={user?.role === "kaafi_system_admin" || user?.role === "kaafi_hr_manager"}
                />

                {/* Tabs */}
                <Tabs defaultValue="overview" data-testid="staff-profile-tabs">
                    <TabsList>
                        <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
                        <TabsTrigger value="personal" data-testid="tab-personal">Personal Info</TabsTrigger>
                        <TabsTrigger value="biometrics" data-testid="tab-biometrics">Biometrics</TabsTrigger>
                        <TabsTrigger value="contracts" data-testid="tab-contracts">Contracts ({contracts.length})</TabsTrigger>
                        <TabsTrigger value="wages" data-testid="tab-wages">Wage Rates</TabsTrigger>
                        {/* Iter60.p36 — Compensation tab now available on
                            all modules (AP / IC / CN). RBAC (wage-rate
                            create/read) is enforced server-side via the
                            /wage-rates endpoints. */}
                        <TabsTrigger value="compensation" data-testid="tab-compensation">Compensation</TabsTrigger>
                        <TabsTrigger value="attendance" data-testid="tab-attendance">Attendance</TabsTrigger>
                        {/* Iter60.p68 — Leave tab visible on Non-Casual
                            profiles only. Casual workers track leave
                            differently via the module Leave &
                            Permissions page. */}
                        {employee.employment_type === "non_casual" && (
                            <TabsTrigger value="leave" data-testid="tab-leave">Leave</TabsTrigger>
                        )}
                        <TabsTrigger value="payroll" data-testid="tab-payroll">Payroll</TabsTrigger>
                        <TabsTrigger value="recurring" data-testid="tab-recurring">Recurring Items</TabsTrigger>
                        <TabsTrigger value="ppe" data-testid="tab-ppe">PPE History</TabsTrigger>
                        <TabsTrigger value="documents" data-testid="tab-documents" disabled>Documents</TabsTrigger>
                    </TabsList>

                    <TabsContent value="overview">
                        <div className="asl-card p-6 space-y-3">
                            <h3 className="text-lg font-bold asl-navy-text">Summary</h3>
                            <p className="text-sm text-[color:var(--asl-muted)] leading-relaxed">
                                {employee.full_name} has been with the {moduleName} module since{" "}
                                {formatDate(employee.hire_date)}. They are currently{" "}
                                <strong className="asl-navy-text">{EMPLOYEE_STATUS_LABEL[employee.status]}</strong>{" "}
                                under a{" "}
                                <strong className="asl-navy-text">
                                    {EMPLOYMENT_TYPE_LABEL[employee.employment_type]}
                                    {employee.worker_subtype
                                        ? ` · ${WORKER_SUBTYPE_LABEL[employee.worker_subtype]}`
                                        : ""}
                                </strong>{" "}
                                arrangement as a {employee.job_title}.
                            </p>
                            <div className="grid grid-cols-3 gap-3 pt-3">
                                <StatCard label="Active contracts" value={contracts.filter((c) => c.status === "active").length} />
                                <StatCard label="Total contracts" value={contracts.length} />
                                <StatCard label="Tenure (days)" value={daysUntil(employee.hire_date) ? Math.abs(daysUntil(employee.hire_date)) : "—"} />
                            </div>

                            {/* Iter54 — Government documents section (moved to Overview per spec) */}
                            <h4 className="text-sm font-bold asl-navy-text mt-4 pt-4 border-t border-[color:var(--asl-border)]" data-testid="profile-gov-docs">Government Documents</h4>
                            <div className="grid grid-cols-2 gap-4 text-sm">
                                <Fact label="CID Reference" value={employee.cid_reference || "—"} mono />
                                <Fact label="CID Date of Issue" value={formatDate(employee.cid_date_of_issue)} />
                                <Fact label="NID Reference" value={employee.nid_reference || employee.national_id || "—"} mono />
                                <Fact label="NID Date of Issue" value={formatDate(employee.nid_date_of_issue)} />
                                <Fact label="NID Expiry Date" value={formatDate(employee.nid_expiry_date)} />
                            </div>

                            {/* Iter54 — ASL Company ID Card section (moved to Overview per spec) */}
                            <h4 className="text-sm font-bold asl-navy-text mt-4 pt-4 border-t border-[color:var(--asl-border)]" data-testid="profile-asl-card">ASL Company ID Card</h4>
                            <div className="grid grid-cols-3 gap-4 text-sm">
                                <Fact label="Issue Date" value={formatDate(employee.asl_id_card_issue_date)} />
                                <Fact label="Expiry Date" value={formatDate(employee.asl_id_card_expiry_date)} />
                                <div>
                                    <div className="text-[10px] text-[color:var(--asl-muted)] uppercase tracking-wider">Status</div>
                                    <IdCardStatusPill status={employee.id_card_status} daysRemaining={employee.id_card_days_remaining} />
                                </div>
                            </div>
                        </div>
                    </TabsContent>

                    <TabsContent value="personal">
                        <div className="asl-card p-6 space-y-3">
                            <h3 className="text-lg font-bold asl-navy-text">Personal Info</h3>
                            <div className="grid grid-cols-2 gap-4 text-sm">
                                <Fact label="Gender" value={employee.gender || "—"} />
                                <Fact label="Date of birth" value={formatDate(employee.date_of_birth)} />
                                <Fact label="Age" value={employee.age != null ? String(employee.age) : "—"} mono />
                                <Fact label="Worker type" value={employee.worker_type === "casual" ? "Casual" : employee.worker_type === "non_casual" ? "Non-Casual" : (employee.employment_type || "—")} />
                                <Fact label="Phone" value={employee.phone || "—"} mono />
                                <Fact label="Email" value={employee.email || "—"} mono />
                                <Fact label="Address" value={employee.address || "—"} />
                                <Fact label="Effective pay rate" value={employee.effective_pay_rate ? `$${Number(employee.effective_pay_rate.amount).toFixed(2)} / ${employee.effective_pay_rate.frequency}` : "—"} mono />
                            </div>
                            {canEdit && (
                                <Button
                                    onClick={() => setEditOpen(true)}
                                    variant="outline"
                                    size="sm"
                                    className="mt-3"
                                    data-testid="staff-personal-edit"
                                >
                                    <Pencil className="h-4 w-4 mr-1" /> Edit personal info
                                </Button>
                            )}
                        </div>

                        {/* Iter60.p12 — Payment Details section on the Personal tab.
                            Read-only for everyone; account number is masked by default
                            with an eye-toggle for Admin/HR (see PaymentDetailsCard). */}
                        <PaymentDetailsCard employee={employee} canReveal={canEdit} />
                    </TabsContent>

                    <TabsContent value="biometrics">
                        <BiometricsTab employee={employee} canEdit={canEdit} onChanged={load} />
                    </TabsContent>

                    <TabsContent value="contracts">
                        <div className="asl-card overflow-hidden">
                            <div className="flex items-center justify-between p-4">
                                <h3 className="text-lg font-bold asl-navy-text">Contracts</h3>
                                {canEdit && (
                                    <Button
                                        size="sm"
                                        className="asl-navy-bg text-white"
                                        onClick={() => setContractOpen(true)}
                                        data-testid="staff-add-contract"
                                    >
                                        <Plus className="h-4 w-4 mr-1" /> Add Contract
                                    </Button>
                                )}
                            </div>
                            {contracts.length === 0 ? (
                                <div className="p-8 text-center text-sm text-[color:var(--asl-muted)]" data-testid="staff-no-contracts">
                                    No contracts on file yet.
                                </div>
                            ) : (
                                <table className="asl-table asl-table--zebra" data-testid="staff-contracts-table">
                                    <thead
                                        className="text-[11px] uppercase tracking-wider text-[color:var(--asl-muted)]"
                                        style={{ background: "var(--asl-navy-50)", borderBottom: "1px solid var(--asl-border)" }}
                                    >
                                        <tr>
                                            <th className="text-left px-4 py-3">Type</th>
                                            <th className="text-left px-4 py-3">Start</th>
                                            <th className="text-left px-4 py-3">End</th>
                                            <th className="text-left px-4 py-3">Salary</th>
                                            <th className="text-left px-4 py-3">Basic</th>
                                            <th className="text-left px-4 py-3">Benefits</th>
                                            <th className="text-left px-4 py-3">Total</th>
                                            <th className="text-left px-4 py-3">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {contracts.map((c) => (
                                            <tr
                                                key={c.id}
                                                className="border-t"
                                                style={{ borderColor: "var(--asl-border)" }}
                                                data-testid={`contract-row-${c.id}`}
                                            >
                                                <td className="px-4 py-3 text-xs asl-navy-text font-medium">
                                                    {CONTRACT_TYPE_LABEL[c.contract_type]}
                                                </td>
                                                <td className="px-4 py-3 mono text-xs">{formatDate(c.start_date)}</td>
                                                <td className="px-4 py-3 mono text-xs">
                                                    {formatDate(c.end_date)}
                                                </td>
                                                <td className="px-4 py-3 text-xs">{SALARY_TYPE_LABEL[c.salary_type]}</td>
                                                <td className="px-4 py-3 mono text-xs">{formatCurrency(c.basic_salary)}</td>
                                                <td className="px-4 py-3 mono text-xs">{formatCurrency(c.benefits_component)}</td>
                                                <td className="px-4 py-3 mono text-xs font-semibold asl-navy-text">
                                                    {formatCurrency(c.total_compensation)}
                                                </td>
                                                <td className="px-4 py-3">
                                                    <span className={`text-xs px-2 py-0.5 rounded-full ${statusBadgeClass(c.status)}`}>
                                                        {CONTRACT_STATUS_LABEL[c.status]}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </TabsContent>

                    <TabsContent value="wages">
                        <div className="asl-card p-6 text-sm text-[color:var(--asl-muted)]">
                            Wage rates are managed at the module + job role level. Visit{" "}
                            <a href="/wage-rates" className="asl-navy-text font-semibold underline">
                                Wage Rates
                            </a>{" "}
                            to view and approve rates that apply to this employee.
                        </div>
                    </TabsContent>

                    {/* Iter60.p36 — Compensation tab available on all
                        modules (AP / IC / CN). See TabsTrigger note. */}
                    <TabsContent value="compensation">
                        <CompensationTab employee={employee} moduleCode={moduleCode} />
                    </TabsContent>

                    <TabsContent value="attendance">
                        <AttendanceTab employeeId={employee.id} />
                    </TabsContent>

                    {/* Iter60.p68 — Leave tab for Non-Casual employees. */}
                    {employee.employment_type === "non_casual" && (
                        <TabsContent value="leave">
                            <StaffLeaveTab
                                employee={employee}
                                moduleCode={moduleCode}
                                onNewLeaveClick={() =>
                                    navigate(
                                        moduleCode
                                            ? `/m/${moduleCode}/leave?new=1&employee=${employee.id}`
                                            : `/leave?new=1&employee=${employee.id}`,
                                    )
                                }
                            />
                        </TabsContent>
                    )}

                    <TabsContent value="payroll">
                        <PayrollTab employeeId={employee.id} />
                    </TabsContent>

                    <TabsContent value="recurring">
                        <RecurringPayrollItems
                            employeeId={employee.id}
                            canEdit={canEdit}
                        />
                    </TabsContent>

                    <TabsContent value="ppe">
                        <PPETab employeeId={employee.id} />
                    </TabsContent>
                </Tabs>

                {/* Danger Zone */}
                <div className="asl-card p-5 border-rose-200" style={{ borderTop: "3px solid var(--asl-crimson)" }} data-testid="staff-danger-zone">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div>
                            <h3 className="text-sm font-semibold uppercase tracking-wider text-rose-700">Danger zone</h3>
                            <p className="text-xs text-[color:var(--asl-muted)] mt-1 max-w-xl">
                                Soft delete hides the employee from default views. Hard delete is irreversible and cascades to contracts, attendance,
                                leave, discipline records, and tasks. Employees with active payroll lines cannot be hard-deleted.
                            </p>
                        </div>
                        <div className="flex items-center gap-2">
                            {employee.deleted_at && (
                                <span className="text-xs text-[color:var(--asl-muted)] mono">
                                    Soft-deleted on {formatDate(employee.deleted_at)}
                                </span>
                            )}
                            <RowActions
                                entity="employees"
                                endpoint="/employees"
                                id={employee.id}
                                label={employee.module_employee_id || employee.full_name}
                                deleted={!!employee.deleted_at}
                                moduleId={employee.module_id}
                                onChange={() => load()}
                                testid="staff-danger-actions"
                            />
                        </div>
                    </div>
                </div>
            </section>

            <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
                <AlertDialogContent data-testid="staff-delete-confirm">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Terminate employee?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This soft-deletes {employee.full_name}{employee.module_employee_id ? ` (${employee.module_employee_id})` : ""} and sets
                            their status to <strong>terminated</strong>. Historical contracts and audit
                            records remain available.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="staff-delete-cancel">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={onDelete}
                            className="asl-crimson-bg text-white"
                            data-testid="staff-delete-confirm-button"
                        >
                            Yes, terminate
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </AppShell>
    );
}

function Fact({ label, value, mono }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-wider text-[color:var(--asl-muted)]">{label}</div>
            <div className={`text-sm ${mono ? "mono" : ""} asl-navy-text`}>{value || "—"}</div>
        </div>
    );
}

function StatCard({ label, value, title, testid }) {
    return (
        <div
            className="rounded-lg p-3"
            style={{ background: "var(--asl-navy-50)" }}
            title={title || undefined}
            data-testid={testid || undefined}
        >
            <div className="text-[10px] uppercase tracking-wider text-[color:var(--asl-muted)]">{label}</div>
            <div className="text-2xl font-bold asl-navy-text mt-1">{value}</div>
        </div>
    );
}

const ATT_STATUS_LABEL = {
    present: "Present", late: "Late", absent: "Absent", partial: "Partial",
    exception: "Exception", weekend: "Weekend", holiday: "Holiday",
    // Iter60.p27 — new attendance status: employee worked on a weekend day.
    weekend_present: "Weekend Present",
    paid_leave: "Paid Leave", unpaid_leave: "Unpaid Leave",
};

function fmtTime(iso) {
    if (!iso) return "—";
    return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function daysAgoIso(n) {
    return new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);
}

function PayrollTab({ employeeId }) {
    const [lines, setLines] = useState([]);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        api.get(`/payroll/employee/${employeeId}/lines`)
            .then(({ data }) => setLines(data))
            .catch((e) => toast.error(formatApiError(e)))
            .finally(() => setLoading(false));
    }, [employeeId]);
    const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;
    if (loading) return <div className="asl-card p-6 text-sm" data-testid="staff-payroll-loading">Loading…</div>;
    if (lines.length === 0) return <div className="asl-card p-10 text-center text-sm text-[color:var(--asl-muted)]" data-testid="staff-payroll-empty">No payroll history.</div>;
    return (
        <div className="asl-card overflow-hidden" data-testid="staff-payroll-tab">
            <table className="asl-table asl-table--zebra" data-testid="staff-payroll-table">
                <thead className="text-[11px] uppercase tracking-wider text-[color:var(--asl-muted)]"
                    style={{ background: "var(--asl-navy-50)", borderBottom: "1px solid var(--asl-border)" }}>
                    <tr>
                        <th className="text-left px-4 py-3">Period</th>
                        <th className="text-left px-4 py-3">Dates</th>
                        <th className="text-right px-4 py-3">Gross</th>
                        <th className="text-right px-4 py-3">Allow</th>
                        <th className="text-right px-4 py-3">Deduct</th>
                        <th className="text-right px-4 py-3">Net</th>
                        <th className="text-left px-4 py-3">Status</th>
                    </tr>
                </thead>
                <tbody>
                    {lines.map((l) => (
                        <tr key={l.id} className="border-t" style={{ borderColor: "var(--asl-border)" }}
                            data-testid={`staff-payroll-row-${l.id}`}>
                            <td className="px-4 py-3 text-xs">{l.period_name}</td>
                            <td className="px-4 py-3 mono text-xs">{l.period_start} → {l.period_end}</td>
                            <td className="px-4 py-3 mono text-xs text-right">{fmt(l.gross_total)}</td>
                            <td className="px-4 py-3 mono text-xs text-right text-emerald-700">{fmt(l.allowances_total)}</td>
                            <td className="px-4 py-3 mono text-xs text-right text-rose-700">{fmt(l.deductions_total)}</td>
                            <td className="px-4 py-3 mono text-xs text-right font-semibold asl-navy-text">{fmt(l.net_payable)}</td>
                            <td className="px-4 py-3 text-xs">{l.payment_status} / {l.period_status}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function AttendanceTab({ employeeId }) {    const [from, setFrom] = useState(daysAgoIso(30));
    const [to, setTo] = useState(new Date().toISOString().slice(0, 10));
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let alive = true;
        setLoading(true);
        api.get("/attendance/daily", {
            params: { employee_id: employeeId, date_from: from, date_to: to },
        })
            .then(({ data }) => { if (alive) setRows(data); })
            .catch((e) => toast.error(formatApiError(e)))
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [employeeId, from, to]);

    const summary = useMemo(() => {
        // Iter60.p27 follow-up — Match payroll's `PRESENT_LIKE`
        // ({present, late, partial, weekend_present}) so the header
        // PRESENT counter reconciles with what Payroll Lines shows.
        // Late is displayed separately as an informational subset.
        const PRESENT_LIKE = new Set(["present", "late", "partial", "weekend_present"]);
        const s = { total: rows.length, present: 0, late: 0, absent: 0, exception: 0, payable: 0 };
        rows.forEach((r) => {
            if (PRESENT_LIKE.has(r.status)) s.present += 1;
            if (r.status === "late") s.late += 1;
            else if (r.status === "absent") s.absent += 1;
            else if (r.status === "exception") s.exception += 1;
            // Iter90 — Multiply by the row's stored weekend/holiday
            // multiplier so the tile reconciles with payroll (which
            // re-derives the same multiplier via `_multiplier_for_day`).
            // Default to 1 when a legacy row has no `multiplier_applied`
            // stamp — the Iter90 CN/IC backfill re-stamps those.
            const mult = Number(r.multiplier_applied || 1) || 1;
            s.payable += Number(r.payable_hours || 0) * mult;
        });
        s.payable = Math.round(s.payable * 10) / 10;
        return s;
    }, [rows]);

    return (
        <div className="space-y-4" data-testid="staff-attendance-tab">
            <div className="asl-card p-4 flex flex-wrap items-end gap-3">
                <div className="space-y-1">
                    <div className="text-[10px] uppercase tracking-wider text-[color:var(--asl-muted)]">From</div>
                    <DateInput value={from} onChange={setFrom}
                        className="w-40" data-testid="staff-att-from" />
                </div>
                <div className="space-y-1">
                    <div className="text-[10px] uppercase tracking-wider text-[color:var(--asl-muted)]">To</div>
                    <DateInput value={to} onChange={setTo}
                        className="w-40" data-testid="staff-att-to" />
                </div>
                <div className="ml-auto grid grid-cols-2 md:grid-cols-5 gap-3 text-xs">
                    <StatCard label="Days" value={summary.total} />
                    <StatCard label="Present" value={summary.present} />
                    <StatCard label="Late" value={summary.late} />
                    <StatCard label="Absent" value={summary.absent} />
                    <StatCard
                        label="Pay hrs"
                        value={summary.payable}
                        title="Includes weekend/holiday multipliers (1.5× etc.). For raw worked hours see the Worked column per row."
                        testid="staff-att-pay-hrs"
                    />
                </div>
            </div>

            <div className="asl-card overflow-hidden">
                {loading ? (
                    <div className="p-6 text-sm text-[color:var(--asl-muted)]" data-testid="staff-att-loading">Loading…</div>
                ) : rows.length === 0 ? (
                    <div className="p-10 text-center text-sm text-[color:var(--asl-muted)]" data-testid="staff-att-empty">
                        No attendance records in this range.
                    </div>
                ) : (
                    <table className="asl-table asl-table--zebra" data-testid="staff-att-table">
                        <thead className="text-[11px] uppercase tracking-wider text-[color:var(--asl-muted)]"
                            style={{ background: "var(--asl-navy-50)", borderBottom: "1px solid var(--asl-border)" }}>
                            <tr>
                                <th className="text-left px-4 py-3">Date</th>
                                <th className="text-left px-4 py-3">In</th>
                                <th className="text-left px-4 py-3">Out</th>
                                <th className="text-left px-4 py-3">Worked</th>
                                <th className="text-left px-4 py-3">Late</th>
                                <th className="text-left px-4 py-3">OT</th>
                                <th className="text-left px-4 py-3">Payable</th>
                                <th className="text-left px-4 py-3">Pay×</th>
                                <th className="text-left px-4 py-3">Status</th>
                                <th className="text-left px-4 py-3">Approved</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => (
                                <tr key={r.id} className="border-t" style={{ borderColor: "var(--asl-border)" }}
                                    data-testid={`staff-att-row-${r.work_date}`}>
                                    <td className="px-4 py-3 mono text-xs">{formatDate(r.work_date)}</td>
                                    <td className="px-4 py-3 mono text-xs">{fmtTime(r.first_clock_in)}</td>
                                    <td className="px-4 py-3 mono text-xs">{fmtTime(r.last_clock_out)}</td>
                                    <td className="px-4 py-3 mono text-xs">{(r.worked_minutes / 60).toFixed(1)}h</td>
                                    <td className="px-4 py-3 mono text-xs">{r.late_minutes ? `${r.late_minutes}m` : "—"}</td>
                                    <td className="px-4 py-3 mono text-xs">{r.overtime_minutes ? `${r.overtime_minutes}m` : "—"}</td>
                                    <td className="px-4 py-3 mono text-xs">{r.payable_hours}h</td>
                                    <td className="px-4 py-3 mono text-xs">{r.multiplier_applied}×</td>
                                    <td className="px-4 py-3">
                                        <span className={`text-xs px-2 py-0.5 rounded-full ${statusBadgeClass(r.status)}`}>
                                            {ATT_STATUS_LABEL[r.status] || r.status}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3">
                                        {r.approved
                                            ? <span className="text-emerald-700 text-xs font-semibold">✓</span>
                                            : <span className="text-[color:var(--asl-muted)] text-xs">—</span>}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}


function BiometricsTab({ employee, canEdit, onChanged }) {
    const [devices, setDevices] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ device_id: "", biometric_user_id: "" });
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        api.get("/devices").then(({ data }) => setDevices(data)).catch(() => {});
    }, []);

    const enrollments = employee?.biometric_enrollments || [];
    const devicesById = useMemo(() => Object.fromEntries(devices.map((d) => [d.id, d])), [devices]);
    const sortedDevices = useMemo(() => {
        // Exclude Mock family — those are test scaffolds and should never be
        // used to enrol a real employee (was the root cause of an earlier
        // primary-id corruption).
        const usable = devices.filter((d) =>
            d.status !== "disabled" && String(d.connection_type || "").toLowerCase() !== "mock"
        );
        const sameModule = usable.filter((d) => d.module_id === employee?.module_id);
        const others = usable.filter((d) => d.module_id !== employee?.module_id);
        return [...sameModule, ...others];
    }, [devices, employee?.module_id]);

    const defaultBioId = (() => {
        if (!employee?.employee_code) return "";
        const m = employee.employee_code.match(/(\d+)/);
        return m ? String(parseInt(m[1], 10)) : "";
    })();

    const openEnroll = () => {
        // Prefill STRICTLY with the current Primary ID when present. Only fall
        // back to employee_code-derived default if the employee has no primary yet.
        const seed = employee?.biometric_user_id
            ? String(employee.biometric_user_id)
            : defaultBioId;
        setForm({
            device_id: sortedDevices[0]?.id || "",
            biometric_user_id: seed,
            confirm_primary_change: false,
        });
        setOpen(true);
    };

    // Devices where the CURRENT primary id is already enrolled — these stop
    // matching attendance the moment the primary id is changed.
    const currentPrimary = String(employee?.biometric_user_id || "");
    const requestedId = String(form.biometric_user_id || "");
    const primaryWillChange = Boolean(currentPrimary) && currentPrimary !== requestedId && Boolean(requestedId);
    const otherDevicesUsingCurrent = useMemo(() => {
        if (!primaryWillChange) return [];
        return enrollments
            .filter((en) => en.device_id !== form.device_id
                && String(en.biometric_user_id || "") === currentPrimary)
            .map((en) => devicesById[en.device_id])
            .filter(Boolean);
    }, [enrollments, form.device_id, currentPrimary, primaryWillChange, devicesById]);

    const submit = async () => {
        if (!form.device_id || !form.biometric_user_id) {
            toast.error("Pick a device and a USERID");
            return;
        }
        if (primaryWillChange && !form.confirm_primary_change) {
            toast.error("Please tick the confirmation box to change the Primary biometric_user_id");
            return;
        }
        setBusy(true);
        try {
            await api.post(`/employees/${employee.id}/enroll-biometric`, {
                device_id: form.device_id,
                biometric_user_id: String(form.biometric_user_id),
                set_as_primary: Boolean(primaryWillChange && form.confirm_primary_change),
            });
            toast.success(primaryWillChange
                ? `Enrolled. Primary ID changed to ${requestedId}.`
                : "Enrolled. Update_user command queued to device.");
            setOpen(false);
            onChanged();
        } catch (e) {
            // If backend still refuses due to primary-id change guard, surface the
            // structured message so admin knows exactly what will break.
            const detail = e?.response?.data?.detail;
            if (detail && typeof detail === "object" && detail.code === "primary_id_change_requires_confirm") {
                toast.error(detail.message || "Confirmation required to change Primary biometric_user_id");
            } else {
                toast.error(formatApiError(e));
            }
        }
        finally { setBusy(false); }
    };

    const remove = async (deviceId) => {
        try {
            await api.delete(`/employees/${employee.id}/enroll-biometric/${deviceId}`);
            toast.success("Enrollment removed");
            onChanged();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const pushUpdate = async (en) => {
        try {
            await api.post(`/devices/${en.device_id}/queue-command`, {
                command: "update_user",
                payload: {
                    biometric_user_id: en.biometric_user_id,
                    name: employee.full_name,
                    employee_id: employee.id,
                },
            });
            toast.success("update_user queued");
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const familyOf = (ct) => {
        if (!ct) return { name: "Device", tone: "neutral" };
        const s = String(ct).toLowerCase();
        if (s.startsWith("hikvision")) return { name: "Hikvision", tone: "crimson" };
        if (s.startsWith("zkteco") || s === "adms_push")
            return { name: "ZKTeco", tone: "navy" };
        if (s === "mock") return { name: "Mock", tone: "neutral" };
        return { name: ct, tone: "neutral" };
    };

    const moduleDevices = useMemo(
        () => devices.filter((d) => d.module_id === employee?.module_id && d.status !== "disabled"),
        [devices, employee?.module_id],
    );
    const hikOnlyInModule = moduleDevices.length > 0 &&
        moduleDevices.every((d) => familyOf(d.connection_type).name === "Hikvision");

    return (
        <div className="asl-card p-6 space-y-4" data-testid="biometrics-tab">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                    <h3 className="text-lg font-bold asl-navy-text inline-flex items-center gap-2">
                        <Fingerprint className="h-5 w-5" /> Biometric Enrollments
                    </h3>
                    <p className="text-xs text-[color:var(--asl-muted)] mt-1 max-w-2xl" data-testid="bio-tab-description">
                        Biometric USERIDs registered for this employee across all devices (ZKTeco and Hikvision).
                        Face and fingerprint templates are captured on the physical device itself. The Primary
                        <span className="mono"> biometric_user_id </span>
                        below is what matches attendance events from any device family.
                    </p>
                </div>
                {canEdit && (
                    <div className="flex flex-col items-end gap-1">
                        <Button size="sm" className="asl-navy-bg text-white" onClick={openEnroll}
                                data-testid="bio-tab-enroll-btn">
                            <Plus className="h-4 w-4 mr-1" /> Enroll on device
                        </Button>
                        {hikOnlyInModule && (
                            <p className="text-[10px] text-[color:var(--asl-crimson)] max-w-[240px] text-right leading-snug"
                               data-testid="bio-tab-hik-only-hint">
                                Hikvision terminals require in-person enrolment at the device menu.
                                Use <span className="mono">Employee No = {employee?.biometric_user_id || defaultBioId || "—"}</span> on the device.
                            </p>
                        )}
                    </div>
                )}
            </div>

            <div className="text-xs text-[color:var(--asl-muted)]">
                Primary <span className="mono">biometric_user_id</span>:{" "}
                <span className="asl-navy-text font-semibold mono">{employee?.biometric_user_id || "—"}</span>
            </div>

            {enrollments.length === 0 ? (
                <div className="text-sm text-[color:var(--asl-muted)] italic py-6 text-center border border-dashed rounded-md"
                     data-testid="bio-tab-empty">
                    Not enrolled on any device yet.
                </div>
            ) : (
                <table className="asl-table asl-table--zebra" data-testid="bio-tab-table">
                    <thead className="text-[11px] uppercase tracking-wider text-[color:var(--asl-muted)]"
                           style={{ background: "var(--asl-navy-50)", borderBottom: "1px solid var(--asl-border)" }}>
                        <tr>
                            <th className="text-left px-3 py-2">Device</th>
                            <th className="text-left px-3 py-2">SN</th>
                            <th className="text-left px-3 py-2">USERID</th>
                            <th className="text-left px-3 py-2">Enrolled</th>
                            <th className="text-right px-3 py-2">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {enrollments.map((en) => {
                            const d = devicesById[en.device_id] || {};
                            return (
                                <tr key={en.device_id} className="border-t" style={{ borderColor: "var(--asl-border)" }}
                                    data-testid={`bio-tab-row-${en.device_id}`}>
                                    <td className="px-3 py-2">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span>{d.device_name || "(deleted device)"}</span>
                                            {d.connection_type && (() => {
                                                const fam = familyOf(d.connection_type);
                                                const cls = fam.tone === "crimson"
                                                    ? "bg-[color:var(--asl-crimson-50)] text-[color:var(--asl-crimson)]"
                                                    : fam.tone === "navy"
                                                        ? "bg-[color:var(--asl-navy-50)] asl-navy-text"
                                                        : "bg-gray-100 text-gray-700";
                                                return (
                                                    <span
                                                        className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${cls}`}
                                                        data-testid={`bio-tab-family-${en.device_id}`}
                                                    >{fam.name}</span>
                                                );
                                            })()}
                                        </div>
                                    </td>
                                    <td className="px-3 py-2 mono text-xs">{d.serial_number || "—"}</td>
                                    <td className="px-3 py-2 mono text-xs">{en.biometric_user_id}</td>
                                    <td className="px-3 py-2 mono text-xs">
                                        {en.enrolled_at ? formatDateTime(en.enrolled_at) : "—"}
                                    </td>
                                    <td className="px-3 py-2 text-right space-x-1">
                                        {canEdit && (
                                            <>
                                                <Button size="sm" variant="outline" onClick={() => pushUpdate(en)}
                                                        data-testid={`bio-tab-push-${en.device_id}`}>
                                                    <Send className="h-3 w-3 mr-1" /> Push
                                                </Button>
                                                <Button size="sm" variant="outline" onClick={() => remove(en.device_id)}
                                                        data-testid={`bio-tab-remove-${en.device_id}`}>
                                                    <Trash2 className="h-3 w-3 mr-1" /> Remove
                                                </Button>
                                            </>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            )}

            <Dialog open={open} onOpenChange={(o) => !o && setOpen(false)}>
                <DialogContent data-testid="bio-tab-enroll-dialog">
                    <DialogHeader><DialogTitle>Enroll {employee?.full_name} on device</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label className="text-xs">Device</Label>
                            <select className="w-full border rounded-md p-2 text-sm mt-1"
                                    value={form.device_id}
                                    onChange={(e) => setForm({ ...form, device_id: e.target.value })}
                                    data-testid="bio-tab-device-select">
                                {sortedDevices.map((d) => {
                                    const fam = familyOf(d.connection_type);
                                    const suffix = fam.name === "Hikvision" ? " · (LAN, may fail)" : "";
                                    return (
                                        <option key={d.id} value={d.id}>
                                            [{fam.name}] {d.device_name} — {d.serial_number}{suffix}
                                        </option>
                                    );
                                })}
                            </select>
                            <p className="text-[10px] text-[color:var(--asl-muted)] mt-1">
                                Devices in this employee&apos;s module are listed first.
                            </p>
                        </div>
                        <div>
                            <Label className="text-xs">USERID on the device</Label>
                            <Input value={form.biometric_user_id} onChange={(e) => setForm({ ...form, biometric_user_id: e.target.value, confirm_primary_change: false })}
                                   placeholder={defaultBioId} data-testid="bio-tab-userid-input" />
                            <p className="text-[10px] text-[color:var(--asl-muted)] mt-1">
                                Must exactly match the USERID entered on the device.
                                Current Primary <span className="mono">biometric_user_id</span>:{" "}
                                <span className="asl-navy-text font-semibold mono">
                                    {currentPrimary || "(none)"}
                                </span>
                            </p>
                        </div>
                        {primaryWillChange && (
                            <div
                                className="border rounded-md p-3 text-xs space-y-2"
                                style={{ background: "var(--asl-crimson-50, #fdecef)", borderColor: "var(--asl-crimson, #A8324A)" }}
                                data-testid="bio-tab-primary-change-warning"
                            >
                                <div className="font-semibold asl-crimson-text">
                                    ⚠ This will change {employee?.full_name}&apos;s Primary <span className="mono">biometric_user_id</span> from{" "}
                                    <span className="mono">{currentPrimary}</span> → <span className="mono">{requestedId}</span>.
                                </div>
                                {otherDevicesUsingCurrent.length > 0 ? (
                                    <div>
                                        <div>The following device(s) already have {employee?.full_name} enrolled with{" "}
                                            <span className="mono">{currentPrimary}</span> and will stop matching attendance until re-enrolled:</div>
                                        <ul className="list-disc ml-5 mt-1">
                                            {otherDevicesUsingCurrent.map((d) => (
                                                <li key={d.id}>
                                                    <span className="mono">{d.device_name}</span> — {d.serial_number}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                ) : (
                                    <div>
                                        Attendance events already ingested under <span className="mono">{currentPrimary}</span>{" "}
                                        remain historical but future scans elsewhere with that ID will land in the Exception Queue.
                                    </div>
                                )}
                                <label className="flex items-start gap-2 cursor-pointer pt-1">
                                    <input
                                        type="checkbox"
                                        className="mt-0.5"
                                        checked={!!form.confirm_primary_change}
                                        onChange={(e) => setForm({ ...form, confirm_primary_change: e.target.checked })}
                                        data-testid="bio-tab-primary-change-confirm"
                                    />
                                    <span>
                                        Yes, I want to change the Primary <span className="mono">biometric_user_id</span> to{" "}
                                        <span className="mono">{requestedId}</span>.
                                    </span>
                                </label>
                            </div>
                        )}
                        <div className="text-[11px] text-[color:var(--asl-muted)] p-2 bg-[color:var(--asl-bg,#F5F6F8)] rounded">
                            Fingerprint and face templates are captured on the physical device (Menu → User Mgt → Add User).
                            After capture, the device pushes the templates here; our system stores them as opaque bytes for now.
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)} data-testid="bio-tab-enroll-cancel">Cancel</Button>
                        <Button
                            onClick={submit}
                            className={primaryWillChange ? "asl-crimson-bg text-white" : "asl-navy-bg text-white"}
                            disabled={busy || (primaryWillChange && !form.confirm_primary_change)}
                            data-testid="bio-tab-enroll-submit"
                        >
                            {primaryWillChange ? "Enroll & update Primary ID" : "Enroll"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

// (helper components below)


function PPETab({ employeeId }) {
    const [items, setItems] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [totals, setTotals] = React.useState({ count: 0, spend: 0, in_circulation: 0 });

    React.useEffect(() => {
        let alive = true;
        setLoading(true);
        api.get(`/ppe/employee/${employeeId}/history`)
            .then(({ data }) => {
                if (!alive) return;
                const rows = data.items || [];
                setItems(rows);
                setTotals({
                    count: rows.length,
                    spend: rows.reduce((a, r) => a + Number(r.total_cost || 0), 0),
                    in_circulation: rows.filter((r) => r.status === "issued").length,
                });
            })
            .catch((err) => toast.error(formatApiError(err)))
            .finally(() => alive && setLoading(false));
        return () => { alive = false; };
    }, [employeeId]);

    const STATUS_TONE = {
        issued: "bg-emerald-50 text-emerald-700",
        returned: "bg-blue-50 text-blue-700",
        replaced: "bg-amber-50 text-amber-700",
        lost: "bg-rose-50 text-rose-700",
        damaged: "bg-rose-50 text-rose-700",
    };
    const fmt = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

    return (
        <div className="space-y-4" data-testid="staff-ppe-tab">
            <div className="grid grid-cols-3 gap-3">
                <div className="asl-card p-3">
                    <div className="text-[10px] uppercase text-[color:var(--asl-muted)]">Records</div>
                    <div className="text-lg font-bold mono asl-navy-text mt-1">{totals.count}</div>
                </div>
                <div className="asl-card p-3">
                    <div className="text-[10px] uppercase text-[color:var(--asl-muted)]">In circulation</div>
                    <div className="text-lg font-bold mono asl-navy-text mt-1">{totals.in_circulation}</div>
                </div>
                <div className="asl-card p-3">
                    <div className="text-[10px] uppercase text-[color:var(--asl-muted)]">Lifetime spend</div>
                    <div className="text-lg font-bold mono asl-crimson-text mt-1">{fmt(totals.spend)}</div>
                </div>
            </div>
            <div className="asl-card overflow-hidden">
                {loading ? <div className="p-4 text-sm text-[color:var(--asl-muted)]">Loading…</div>
                    : items.length === 0 ? <div className="p-6 text-sm text-[color:var(--asl-muted)]" data-testid="staff-ppe-empty">No PPE issuances on file for this employee.</div>
                    : <table className="asl-table asl-table--zebra" data-testid="staff-ppe-table">
                        <thead className="text-[11px] uppercase tracking-wider text-[color:var(--asl-muted)]" style={{ background: "var(--asl-navy-50)" }}>
                            <tr>
                                <th className="text-left px-3 py-2">Date</th>
                                <th className="text-left px-3 py-2">Item</th>
                                <th className="text-left px-3 py-2">Size</th>
                                <th className="text-right px-3 py-2">Qty</th>
                                <th className="text-right px-3 py-2">Total</th>
                                <th className="text-left px-3 py-2">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((r) => (
                                <tr key={r.id} className="border-t" style={{ borderColor: "var(--asl-border)" }} data-testid={`staff-ppe-row-${r.id}`}>
                                    <td className="px-3 py-2 mono text-xs">{formatDate(r.issue_date)}</td>
                                    <td className="px-3 py-2 text-xs">
                                        <span className="asl-navy-text font-medium">{r.item_name}</span>{" "}
                                        <span className="text-[10px] text-[color:var(--asl-muted)] uppercase">· {(r.item_type || "").replace(/_/g, " ")}</span>
                                    </td>
                                    <td className="px-3 py-2 text-xs">{r.size || "—"}</td>
                                    <td className="px-3 py-2 mono text-xs text-right">{r.quantity}</td>
                                    <td className="px-3 py-2 mono text-xs text-right asl-navy-text font-semibold">{fmt(r.total_cost)}</td>
                                    <td className="px-3 py-2">
                                        <span className={`text-[10px] uppercase font-bold rounded-full px-2 py-0.5 ${STATUS_TONE[r.status] || "bg-gray-100"}`}>{r.status}</span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>}
            </div>
        </div>
    );
}


// Iter16 — Compensation tab. Shows current effective rate + full
// history (past/current/future-effective) + "Assign new rate" launcher.
// Iter60.p36 — Enabled for AP / IC / CN (was IC-only in the initial
// build; the underlying wage_rates API is module-agnostic).
function CompensationTab({ employee, moduleCode }) {
    const [current, setCurrent] = useState(null);
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newRateOpen, setNewRateOpen] = useState(false);
    const canWrite = useHasPermission("wage_rates.write", employee.module_id || null);

    const load = React.useCallback(async () => {
        setLoading(true);
        try {
            const [effResp, ratesResp] = await Promise.all([
                api.get(`/employees/${employee.id}/effective-rate`),
                api.get("/wage-rates", { params: { module_id: employee.module_id, employee_id: employee.id, include_deleted: true } }),
            ]);
            setCurrent(effResp.data);
            setHistory((ratesResp.data || []).filter((r) => r.employee_id === employee.id));
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    }, [employee.id, employee.module_id]);

    useEffect(() => { load(); }, [load]);

    return (
        <div className="space-y-6" data-testid="compensation-tab">
            <div className="asl-card p-5">
                <div className="flex items-start justify-between">
                    <div>
                        <div className="text-[10px] uppercase tracking-wider text-[color:var(--asl-muted)]">Current effective rate</div>
                        {loading ? (
                            <div className="text-sm text-[color:var(--asl-muted)] mt-1">Loading…</div>
                        ) : current && current.amount != null ? (
                            <div className="mt-1">
                                <span className="text-3xl font-semibold asl-navy-text mono" data-testid="compensation-amount">
                                    ${Number(current.amount).toFixed(2)}
                                </span>
                                <span className="text-sm text-[color:var(--asl-muted)] ml-1">/ {current.frequency}</span>
                                <div className="mt-1 flex items-center gap-2">
                                    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${current.source === "employee_specific" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-blue-50 text-blue-700 border border-blue-200"}`}>
                                        <span className={`w-1.5 h-1.5 rounded-full ${current.source === "employee_specific" ? "bg-emerald-500" : "bg-blue-500"}`} />
                                        {current.source === "employee_specific" ? "Person-specific" : "Role default"}
                                    </span>
                                    {current.effective_date && (
                                        <span className="text-[10px] text-[color:var(--asl-muted)]">since {current.effective_date}</span>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="text-sm text-[color:var(--asl-muted)] italic mt-1" data-testid="compensation-none">No effective rate yet.</div>
                        )}
                    </div>
                    {canWrite && (
                        <Button size="sm" className="asl-navy-bg text-white" onClick={() => setNewRateOpen(true)} data-testid="compensation-assign-new">
                            + Assign new rate
                        </Button>
                    )}
                </div>
            </div>

            <div className="asl-card overflow-hidden">
                <div className="px-4 py-3 border-b" style={{ borderColor: "var(--asl-border)" }}>
                    <div className="text-[10px] uppercase tracking-wider text-[color:var(--asl-muted)]">Rate history for this person</div>
                </div>
                {history.length === 0 ? (
                    <div className="p-6 text-sm text-[color:var(--asl-muted)] italic" data-testid="compensation-history-empty">
                        No person-specific rates yet. This employee is paid based on the role default.
                    </div>
                ) : (
                    <table className="asl-table text-xs" data-testid="compensation-history-table">
                        <thead className="text-[10px] uppercase text-[color:var(--asl-muted)]" style={{ background: "var(--asl-navy-50)" }}>
                            <tr>
                                <th className="text-left px-4 py-2">Effective</th>
                                <th className="text-left px-4 py-2">Job role</th>
                                <th className="text-left px-4 py-2">Category</th>
                                <th className="text-left px-4 py-2">Amount</th>
                                <th className="text-left px-4 py-2">Frequency</th>
                                <th className="text-left px-4 py-2">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {history.slice().sort((a, b) => (b.effective_date || "").localeCompare(a.effective_date || "")).map((r) => {
                                const amt = r.hourly_rate ?? r.daily_rate ?? r.monthly_rate;
                                const isFuture = r.effective_date && r.effective_date > new Date().toISOString().slice(0, 10);
                                return (
                                    <tr key={r.id} className="border-t" style={{ borderColor: "var(--asl-border)" }} data-testid={`compensation-rate-row-${r.id}`}>
                                        <td className="px-4 py-2 mono">{r.effective_date}{isFuture && <span className="ml-1 text-[9px] uppercase text-amber-700">upcoming</span>}</td>
                                        <td className="px-4 py-2">{r.job_role}</td>
                                        <td className="px-4 py-2 text-[10px]">{r.worker_category}</td>
                                        <td className="px-4 py-2 mono">${Number(amt).toFixed(2)}</td>
                                        <td className="px-4 py-2">{r.payment_frequency}</td>
                                        <td className="px-4 py-2">
                                            <span className={`text-[10px] uppercase px-1.5 py-0.5 rounded ${r.status === "approved" ? "bg-emerald-50 text-emerald-700" : r.status === "inactive" ? "bg-gray-100 text-gray-600" : "bg-amber-50 text-amber-700"}`}>
                                                {r.status}
                                            </span>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            <WageRateFormDialog
                open={newRateOpen}
                onOpenChange={setNewRateOpen}
                onSaved={load}
                icEmployees={[employee]}
                isICScope={(moduleCode || "").toUpperCase() === "IC"}
                defaultModuleId={employee.module_id}
                initial={{
                    module_id: employee.module_id,
                    job_role: employee.job_title || "",
                    worker_category: employee.employment_type === "casual"
                        ? "casual"
                        : `non_casual_${employee.worker_subtype || "manual"}`,
                    employee_id: employee.id,
                    payment_frequency: "monthly",
                    effective_date: new Date().toISOString().slice(0, 10),
                }}
            />
        </div>
    );
}


// Iter54 — Warning banner shown near the top of the profile when the ASL ID
// card is expiring soon or already expired. Uses the server-computed
// id_card_status + id_card_days_remaining to stay in sync with the backend.
function IdCardBanner({ employee }) {
    const status = employee?.id_card_status;
    if (!status || (status !== "expiring_soon" && status !== "expired")) return null;
    const days = employee.id_card_days_remaining;
    const exp = employee.asl_id_card_expiry_date;
    const isExpired = status === "expired";
    const tone = isExpired ? "border-rose-300 bg-rose-50 text-rose-900" : "border-amber-300 bg-amber-50 text-amber-900";
    const line1 = isExpired
        ? `ASL Company ID Card expired ${Math.abs(days || 0)} day(s) ago.`
        : `ASL Company ID Card expires in ${days} day(s).`;
    return (
        <div className={`mb-4 rounded-lg border p-3 ${tone}`} data-testid="id-card-banner" data-status={status}>
            <div className="text-sm font-semibold">{line1}</div>
            {exp && (
                <div className="text-xs mt-1">
                    Please renew before <span className="mono">{new Date(exp).toLocaleDateString("en-GB")}</span>.
                </div>
            )}
        </div>
    );
}

function IdCardStatusPill({ status, daysRemaining }) {
    const map = {
        valid:          { txt: "Valid",        cls: "bg-emerald-100 text-emerald-800" },
        expiring_soon:  { txt: "Expiring soon",cls: "bg-amber-100 text-amber-800" },
        expired:        { txt: "Expired",      cls: "bg-rose-100 text-rose-800" },
        unknown:        { txt: "Not set",      cls: "bg-slate-100 text-slate-600" },
    };
    const conf = map[status] || map.unknown;
    return (
        <span className={`inline-flex items-center gap-2 mt-1 text-[11px] px-2 py-0.5 rounded-full ${conf.cls}`} data-testid="profile-asl-card-status">
            <span className="font-semibold">{conf.txt}</span>
            {typeof daysRemaining === "number" && (
                <span className="mono">
                    {daysRemaining >= 0 ? `${daysRemaining}d left` : `${Math.abs(daysRemaining)}d over`}
                </span>
            )}
        </span>
    );
}


/**
 * Iter60.p12 — Payment Details card on the Personal tab of the profile.
 *
 * - Renders NOTHING when the employee has no bank data set (server-side
 *   `has_bank_details=false` OR both bank_name/bank_account_number are
 *   empty), keeping the profile uncluttered for the common case.
 * - Account number is masked (`•••• 1234`) by default. Admin/HR (i.e.
 *   `canReveal=true`) get an eye toggle to reveal the full value.
 * - Everything below is read-only. Editing happens via the same
 *   EmployeeFormDialog accordion the create flow uses.
 */
function PaymentDetailsCard({ employee, canReveal }) {
    const [reveal, setReveal] = React.useState(false);
    if (!employee) return null;
    const {
        bank_name,
        bank_account_number,
        bank_account_holder_name,
        bank_branch,
        bank_swift_code,
        bank_notes,
        has_bank_details,
    } = employee;

    // Nothing to show if the row has no bank info at all.
    const anyBank = Boolean(
        bank_name || bank_account_number || bank_account_holder_name
        || bank_branch || bank_swift_code || bank_notes
    );
    if (!anyBank) return null;

    // Compute the masked view for the account number.
    const acct = (bank_account_number || "").trim();
    const masked = acct
        ? acct.length <= 4
            ? "•".repeat(Math.max(0, acct.length))
            : "•••• " + acct.slice(-4)
        : "—";

    return (
        <div className="asl-card p-6 mt-4 space-y-3" data-testid="profile-payment-details">
            <div className="flex items-center gap-2">
                <Landmark className="h-4 w-4 asl-navy-text" aria-hidden="true" />
                <h3 className="text-lg font-bold asl-navy-text">Payment Details</h3>
                {has_bank_details && (
                    <span
                        className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 uppercase tracking-wider"
                        data-testid="payment-status-complete"
                    >
                        Bank set
                    </span>
                )}
                {!has_bank_details && (
                    <span
                        className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 uppercase tracking-wider"
                        data-testid="payment-status-incomplete"
                    >
                        Incomplete
                    </span>
                )}
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm">
                <Fact label="Bank" value={bank_name || "—"} />
                <div>
                    <div className="text-[10px] text-[color:var(--asl-muted)] uppercase tracking-wider">Account Number</div>
                    <div className="mt-1 flex items-center gap-2">
                        <span className="mono text-sm" data-testid="payment-account-number">
                            {reveal ? (acct || "—") : masked}
                        </span>
                        {canReveal && acct && (
                            <button
                                type="button"
                                className="text-[color:var(--asl-muted)] hover:asl-crimson-text"
                                onClick={() => setReveal((r) => !r)}
                                aria-label={reveal ? "Hide account number" : "Reveal account number"}
                                data-testid="payment-account-reveal-toggle"
                                data-revealed={reveal ? "true" : "false"}
                            >
                                {reveal ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                        )}
                    </div>
                </div>
                <Fact label="Account Holder" value={bank_account_holder_name || employee.full_name || "—"} />
                <Fact label="Branch" value={bank_branch || "—"} />
                <Fact label="SWIFT / BIC" value={bank_swift_code || "—"} mono />
                {bank_notes && (
                    <div className="col-span-2">
                        <div className="text-[10px] text-[color:var(--asl-muted)] uppercase tracking-wider">Notes</div>
                        <div className="text-sm whitespace-pre-line" data-testid="payment-notes">{bank_notes}</div>
                    </div>
                )}
            </div>
        </div>
    );
}
